// src/components/AvatarMenu.jsx
import React from "react";

/**
 * Reusable account dropdown.
 *
 * Props:
 * - user: { username?: string, email?: string }
 * - onProfile: () => void
 * - onLogout: () => void
 * - labelPrefix?: string  // default "Welcome,"
 *
 * Styling hooks (already in your CSS):
 * - .profile-chip, .profile-chip-avatar, .profile-chip-name, .profile-chip-caret
 * - .profile-menu, .profile-menu-item, .profile-menu-item.danger
 * - .nav-button  (for the trigger button)
 */
export default function AvatarMenu({ user, onProfile, onLogout, labelPrefix = "Welcome," }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);

  // close on outside click + Esc
  React.useEffect(() => {
    const handleDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const handleEsc = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handleDocClick);
    document.addEventListener("keydown", handleEsc);
    return () => {
      document.removeEventListener("mousedown", handleDocClick);
      document.removeEventListener("keydown", handleEsc);
    };
  }, []);

  if (!user) return null;

  const initials = (user?.username || user?.email || "U").trim()[0]?.toUpperCase() || "U";
  const displayName = user?.username || user?.email || "User";

  return (
    <div className="profile-chip" ref={ref}>
      <button
        type="button"
        className="nav-button"
        style={{ display: "flex", alignItems: "center", gap: 8 }}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        title="Account"
      >
        <span className="profile-chip-avatar">{initials}</span>
        <span className="profile-chip-name">
          {labelPrefix}&nbsp;{displayName}!
        </span>
        <span className="profile-chip-caret">▾</span>
      </button>

      {open && (
        <div className="profile-menu" role="menu">
          <button
            className="profile-menu-item"
            onClick={() => {
              setOpen(false);
              onProfile?.();
            }}
          >
            Profile
          </button>
          <button
            className="profile-menu-item danger"
            onClick={() => {
              setOpen(false);
              onLogout?.();
            }}
          >
            Logout
          </button>
        </div>
      )}
    </div>
  );
}
